import { useMemo, useEffect } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { Switch } from '@components/Switch';
import { MultiSelect } from '@components/MultiSelect';
import ComboLocationMultiselect, { LOCATION_TYPE } from '@features/ComboLocationMultiselect';
import { Select } from '@components/Select';
import { ColorPicker } from '@components/ColorPicker';
import { FiltersIntegrationType } from '@constants/integrations';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { ReactComponent as WidgetSingle } from 'assets/images/widget-single.svg';
import gridWidget from 'assets/images/widget-grid.svg';
import Code from './Code';
import { Modal, MainContent, Form, SubContent } from './styles';

const reviesToShowOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '8', label: '8' },
    { value: '16', label: '16' },
    { value: '24', label: '24' },
];

const EditModal = ({ modal, setModal, account, view, locations, brands }) => {
    const { t } = useTranslation();
    const { mapSelectedLocationsToFormValue, getLocationsOptions } = useLocations();

    const WidgetsSchema = yup.object().shape({
        locations: yup.array().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATIONS || (locationType === LOCATION_TYPE.BRANDS && !brandAccess),
            then: yup.array().min(1, 'validation.required'),
            otherwise: yup.array(),
        }),
        brands: yup.array().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRANDS && brandAccess,
            then: yup.array().min(1, 'validation.required'),
            otherwise: yup.array(),
        }),
        integrations: yup.array().min(1, 'validation.required'),
        locationType: yup.string(),
        backgroundColor: yup.string().validateColor('validation.hexFormat'),
    });

    const resolver = useYupValidationResolver(WidgetsSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        formState: { isValid },
    } = useForm({
        mode: 'onBlur',
        resolver,
        defaultValues: {
            sorting: '0',
            count: '24',
            hideEmptyReviews: true,
            brands: [],
            locations: [],
            integrations: [],
            backgroundColor: '#F1F1F1',
            locationType: LOCATION_TYPE.BRANDS,
            brandAccess: false,
            selectedLocationsIds: [],
        },
    });

    const brandAccess = watch('brandAccess');
    const selectedLocationsIds = watch('selectedLocationsIds');
    const locationType = watch('locationType');

    useEffect(() => {
        let availableCompaniesIds = [];
        // INFO: if not location based user or admin
        if (brandAccess) {
            if (locationType === LOCATION_TYPE.BRANDS) {
                availableCompaniesIds = brands
                    .map(item => item.locations)
                    .flat()
                    .map(item => item.id);
                setValue(
                    'brands',
                    brands.map(item => item.id),
                    { shouldValidate: true },
                );
                setValue('locations', [], { shouldValidate: true });
            } else {
                availableCompaniesIds = locations.map(item => item.companyId);
                setValue('locations', mapSelectedLocationsToFormValue(availableCompaniesIds), {
                    shouldValidate: true,
                });
                setValue('brands', [], { shouldValidate: true });
            }
        } else {
            availableCompaniesIds = locations.map(item => item.companyId);
            setValue('locations', mapSelectedLocationsToFormValue(availableCompaniesIds), {
                shouldValidate: true,
            });
            setValue('brands', [], { shouldValidate: true });
        }
        const integrations = [
            ...new Set(
                locations
                    .filter(item => availableCompaniesIds.includes(item.companyId))
                    .map(company => company.profiles)
                    .flat()
                    .filter(p => p.type in FiltersIntegrationType)
                    .map(p => p.type),
            ),
        ];
        // INFO: set selected locations ids for depended fields
        setValue('selectedLocationsIds', availableCompaniesIds, { shouldValidate: true });
        setValue('integrations', integrations, { shouldValidate: true });
    }, [brands, locations, brandAccess, locationType]);

    const locationsOptions = useMemo(() => getLocationsOptions(), []);

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));

    const integrationsOptions = useMemo(
        () =>
            [
                ...new Set(
                    locations
                        .filter(item => selectedLocationsIds.includes(item.companyId))
                        .map(company => company.profiles)
                        .flat()
                        .filter(p => p.type in FiltersIntegrationType)
                        .map(p => p.type),
                ),
            ].map(type => ({ value: type, label: FiltersIntegrationType[type] })),
        [selectedLocationsIds],
    );

    const onCloseModal = () => setModal({ open: false, type: null, view: view.FORM });

    const handleChangeView = () => setModal({ ...modal, view: modal.view === view.CODE ? view.FORM : view.CODE });

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onCloseModal}
            title={
                modal.view === view.CODE
                    ? t('ShowcaseWidgets.widgetCode')
                    : modal.type === 'popup'
                    ? t('ShowcaseWidgets.popupWidget')
                    : t('ShowcaseWidgets.gridWidget')
            }
            onPrimaryAction={modal.view === view.FORM ? handleChangeView : onCloseModal}
            primaryActionText={modal.view === view.FORM ? t('buttons.getCode') : t('buttons.done')}
            primaryActionDisabled={!isValid}
            onSecondaryAction={modal.view === view.FORM ? onCloseModal : handleChangeView}
            secondaryActionText={modal.view === view.FORM ? t('buttons.cancel') : t('buttons.back')}
        >
            <FormProvider {...{ control, setValue, watch, getValues }}>
                <MainContent>
                    {modal.view === view.FORM && modal.open && (
                        <>
                            <Form>
                                <ComboLocationMultiselect
                                    brandsOptions={brandsOptions}
                                    locationsOptions={locationsOptions}
                                    updatedLocationsCb={locationsIds => {
                                        const integrations = [
                                            ...new Set(
                                                locations
                                                    .filter(item => locationsIds.includes(item.companyId))
                                                    .map(company => company.profiles)
                                                    .flat()
                                                    .filter(p => p.type in FiltersIntegrationType)
                                                    .map(p => p.type),
                                            ),
                                        ];
                                        setValue('integrations', integrations, { shouldValidate: true });
                                    }}
                                />
                                <Controller
                                    control={control}
                                    name="integrations"
                                    render={({ field, fieldState: { error }, ref }) => (
                                        <MultiSelect
                                            {...field}
                                            ref={ref}
                                            onChange={value =>
                                                setValue('integrations', value, { shouldValidate: true })
                                            }
                                            options={integrationsOptions}
                                            label={t('common.reviewSites')}
                                            countable
                                            required
                                            error={error}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="count"
                                    render={({ field, ref }) => (
                                        <Select
                                            {...field}
                                            ref={ref}
                                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                            onChange={({ target: { value } }) => {
                                                setValue('count', value, { shouldDirty: true });
                                            }}
                                            options={reviesToShowOptions}
                                            label={t('ShowcaseWidgets.reviewsToShow')}
                                            fullWidth
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="sorting"
                                    render={({ field, ref }) => (
                                        <Select
                                            {...field}
                                            ref={ref}
                                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                            onChange={({ target: { value } }) => {
                                                setValue('sorting', value, { shouldDirty: true });
                                            }}
                                            options={[
                                                { value: '0', label: t('ShowcaseWidgets.random') },
                                                { value: '1', label: t('ShowcaseWidgets.recent') },
                                            ]}
                                            label={t('ShowcaseWidgets.reviewsSorting')}
                                            fullWidth
                                        />
                                    )}
                                />
                                {modal.type !== 'popup' && (
                                    <Controller
                                        control={control}
                                        name="backgroundColor"
                                        render={({ field, ref }) => (
                                            <ColorPicker
                                                ref={ref}
                                                inputProps={{
                                                    ...field,
                                                    onChange: e => {
                                                        const val = e?.target?.value ? e.target.value : e;
                                                        field.onChange(val);
                                                    },
                                                }}
                                                label={t('common.background')}
                                            />
                                        )}
                                    />
                                )}
                            </Form>
                            <Controller
                                control={control}
                                name="hideEmptyReviews"
                                render={({ field, ref }) => (
                                    <Switch
                                        {...field}
                                        ref={ref}
                                        checked={field.value}
                                        handleChange={event => {
                                            setValue('hideEmptyReviews', event.target.checked, { shouldDirty: true });
                                        }}
                                        label={t('ShowcaseWidgets.hideEmptyReviews')}
                                    />
                                )}
                            />
                        </>
                    )}
                    {modal.view === view.CODE && modal.open && (
                        <Code widgetKey={account?.widgetKey} type={modal.type} />
                    )}
                </MainContent>
            </FormProvider>
            <SubContent>
                {modal.type === 'popup' ? <WidgetSingle /> : <img src={gridWidget} alt="grid-widfget" />}
            </SubContent>
        </Modal>
    );
};

export default EditModal;
